import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarClock,
  Plus,
  Edit2,
  Trash2,
  Check,
  AlertTriangle,
  ShieldAlert,
  Star,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { Spinner, Modal, useToast } from '@centraoneplus/ui';
import {
  CARD,
  CARD_CLASS,
  CardFoot,
  Checkbox,
  ColumnHeader,
  EmptyState,
  IconButton,
  Input,
  Notice,
  StatusChip,
  Textarea,
  Toggle,
  Button as KitButton,
} from '@shared/ui/kit';
import {
  FieldLabel,
  FILTER_SELECT,
  SectionHead,
  SettingsCard,
} from './chrome';
import { useBusinessHoursStore, CreateBusinessHoursDto } from '@stores/businessHoursStore';
import type {
  BusinessHoursSchedule,
  BusinessHoursUsage,
  DayOfWeek,
  WeeklySchedule,
  Holiday,
} from '@services/businessHours.service';
import { isSamePayload } from '@utils/formDirty';
import { ThemedSelect } from '@components/common/ThemedSelect';

const DAYS: { key: DayOfWeek; label: string }[] = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

// Common IANA timezones (same set offered in Account settings).
const TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
];

function createDefaultSchedule(): WeeklySchedule {
  const work = () => ({ enabled: true, slots: [{ start: '09:00', end: '17:00' }] });
  const off = () => ({ enabled: false, slots: [] });
  return {
    monday: work(),
    tuesday: work(),
    wednesday: work(),
    thursday: work(),
    friday: work(),
    saturday: off(),
    sunday: off(),
  };
}

/** A 34px field for the time/date/text inputs that sit inside a table row. */
const TIME_INPUT =
  'h-[30px] rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-[10px] ' +
  'text-right text-[12.5px] tabular-nums text-[var(--co-ink)] outline-none transition-colors ' +
  'placeholder-[var(--co-ink-4)] focus:border-[var(--co-brass)]';

/** Minutes from midnight for an `HH:mm` value; 0 for anything unparseable. */
function minutesFromMidnight(value: string): number {
  const [h, m] = String(value ?? '').split(':');
  const hours = Number(h);
  const mins = Number(m);
  if (!Number.isFinite(hours) || !Number.isFinite(mins)) return 0;
  return hours * 60 + mins;
}

/** Signed span in minutes. Negative or zero means the row does not run forwards. */
function minutesBetween(start: string, end: string): number {
  return minutesFromMidnight(end) - minutesFromMidnight(start);
}

/**
 * Where a day's open span sits in the 24 hours, as percentages.
 *
 * `null` when the end is at or before the start. That case is NOT redrawn as an
 * overnight shift: nothing in the schedule says a slot may wrap past midnight,
 * and inventing that reading here would draw a bar for hours the SLA clock does
 * not actually count.
 */
function daySpan(start: string, end: string): { left: number; width: number } | null {
  const from = minutesFromMidnight(start);
  const span = minutesBetween(start, end);
  if (span <= 0) return null;
  return { left: (from / 1440) * 100, width: (span / 1440) * 100 };
}

/** `45 hours`, `7.5 hours`, `1 hour` — never a bare float. */
function formatHours(hours: number): string {
  const rounded = Math.round(hours * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${text} hour${rounded === 1 ? '' : 's'}`;
}

interface BusinessHoursFormProps {
  schedule?: BusinessHoursSchedule;
  onSubmit: (data: CreateBusinessHoursDto) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

function BusinessHoursForm({ schedule, onSubmit, onCancel, isLoading }: BusinessHoursFormProps) {
  const [name, setName] = useState(schedule?.name || '');
  const [description, setDescription] = useState(schedule?.description || '');
  const [timezone, setTimezone] = useState(schedule?.timezone || 'UTC');
  const [isDefault, setIsDefault] = useState(schedule?.isDefault || false);
  const [weekly, setWeekly] = useState<WeeklySchedule>(schedule?.schedule || createDefaultSchedule());
  const [holidays, setHolidays] = useState<Holiday[]>(schedule?.holidays || []);

  const toggleDay = (day: DayOfWeek) => {
    setWeekly((prev) => {
      const current = prev[day];
      const enabled = !current.enabled;
      return {
        ...prev,
        [day]: {
          enabled,
          slots: enabled ? [current.slots[0] ?? { start: '09:00', end: '17:00' }] : current.slots,
        },
      };
    });
  };

  const updateTime = (day: DayOfWeek, field: 'start' | 'end', value: string) => {
    setWeekly((prev) => {
      const slot = prev[day].slots[0] ?? { start: '09:00', end: '17:00' };
      return { ...prev, [day]: { ...prev[day], slots: [{ ...slot, [field]: value }] } };
    });
  };

  const addHoliday = () => setHolidays((prev) => [...prev, { name: '', date: '', recurring: false }]);
  const updateHoliday = (index: number, field: keyof Holiday, value: string | boolean) =>
    setHolidays((prev) => prev.map((h, i) => (i === index ? { ...h, [field]: value } : h)));
  const removeHoliday = (index: number) =>
    setHolidays((prev) => prev.filter((_, i) => i !== index));

  // The exact payload this form would submit — also the left-hand side of the
  // dirty check, so what we compare is what we'd send.
  const payload: CreateBusinessHoursDto = useMemo(
    () => ({
      name: name.trim(),
      description: description.trim() || undefined,
      timezone,
      isDefault,
      schedule: weekly,
      holidays: holidays.filter((h) => h.name.trim() && h.date),
    }),
    [name, description, timezone, isDefault, weekly, holidays],
  );

  // Edit mode: nothing to save until a value actually differs from the schedule
  // as it stands in the list. Create mode has no baseline, so it's always "dirty".
  const isDirty = useMemo(() => {
    if (!schedule) return true;
    const original: CreateBusinessHoursDto = {
      name: schedule.name.trim(),
      description: schedule.description?.trim() || undefined,
      timezone: schedule.timezone,
      isDefault: schedule.isDefault,
      schedule: schedule.schedule,
      holidays: (schedule.holidays || []).filter((h) => h.name.trim() && h.date),
    };
    return !isSamePayload(original, payload);
  }, [schedule, payload]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(payload);
  };

  // What the week actually adds up to, read straight off the form's own values.
  // Derived, never stored: a saved "45 hours" that disagreed with the rows under
  // it would be the one number nobody could reconcile.
  const weeklyHours = useMemo(
    () =>
      DAYS.reduce((total, { key }) => {
        const day = weekly[key];
        if (!day?.enabled) return total;
        const slot = day.slots[0];
        if (!slot) return total;
        return total + Math.max(0, minutesBetween(slot.start, slot.end)) / 60;
      }, 0),
    [weekly],
  );
  const openDays = DAYS.filter(({ key }) => weekly[key]?.enabled).length;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic info */}
      <div className="space-y-4">
        <div>
          <FieldLabel htmlFor="bh-name">Schedule name *</FieldLabel>
          <div className="mt-[7px]">
            <Input
              id="bh-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., India Support Hours"
              required
            />
          </div>
        </div>

        <div>
          <FieldLabel htmlFor="bh-description">Description</FieldLabel>
          <div className="mt-[7px]">
            <Textarea
              id="bh-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={2}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <FieldLabel htmlFor="bh-timezone">Time zone *</FieldLabel>
            <div className="mt-[7px]">
              <ThemedSelect
                id="bh-timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className={`${FILTER_SELECT} w-full`}
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </ThemedSelect>
            </div>
            {/* Every deadline below is evaluated in this zone, so it is stated
                once here rather than repeated on each row. */}
            <p className="mt-[5px] text-[11.5px] leading-[1.45] text-[var(--co-ink-3)]">
              Every hour below is read in this zone.
            </p>
          </div>
          <div className="flex items-end">
            <div className="flex items-center gap-[10px] pb-[6px]">
              <Checkbox
                label="Set as default schedule"
                checked={isDefault}
                onChange={setIsDefault}
              />
              <span className="text-[12.5px] text-[var(--co-ink-2)]">Set as default</span>
            </div>
          </div>
        </div>
      </div>

      {/* Weekly schedule */}
      <div className={`overflow-hidden ${CARD_CLASS}`} style={CARD}>
        <div className="border-b border-[var(--co-line-subtle)] px-[14px] py-[13px]">
          <h4 className="text-[13.5px] font-semibold text-[var(--co-ink)]">Weekly schedule</h4>
          <p className="mt-[4px] text-[12px] leading-[1.5] text-[var(--co-ink-3)]">
            SLA clocks on policies that follow this schedule stop outside these hours. A four-hour
            target set at 16:00 on the last open day is due the next morning, not that evening.
          </p>
        </div>

        <ColumnHeader
          columns={[
            { id: 'day', label: 'Day', width: 130 },
            { id: 'open', label: 'Open', width: 74 },
            { id: 'from', label: 'From', width: 118, align: 'right' },
            { id: 'to', label: 'To', width: 118, align: 'right' },
            { id: 'hours', label: 'Hours' },
          ]}
        />

        <div>
          {DAYS.map(({ key, label }) => {
            const day = weekly[key];
            const slot = day.slots[0];
            const span = day.enabled && slot ? daySpan(slot.start, slot.end) : null;
            return (
              <div
                key={key}
                role="row"
                className="flex items-center gap-3 border-b border-[var(--co-line-subtle)] px-[14px] py-[10px] last:border-b-0"
                style={{ background: day.enabled ? undefined : 'var(--co-sunken)' }}
              >
                <div
                  className="flex-none text-[13px]"
                  style={{
                    width: 130,
                    color: day.enabled ? 'var(--co-ink)' : 'var(--co-ink-3)',
                    fontWeight: day.enabled ? 600 : 400,
                  }}
                >
                  {label}
                </div>

                <div className="flex-none" style={{ width: 74 }}>
                  <Toggle
                    label={`${label} open`}
                    on={day.enabled}
                    onChange={() => toggleDay(key)}
                  />
                </div>

                {day.enabled ? (
                  <>
                    <div className="flex flex-none justify-end" style={{ width: 118 }}>
                      <input
                        type="time"
                        aria-label={`${label} opening time`}
                        value={slot?.start ?? '09:00'}
                        onChange={(e) => updateTime(key, 'start', e.target.value)}
                        className={TIME_INPUT}
                      />
                    </div>
                    <div className="flex flex-none justify-end" style={{ width: 118 }}>
                      <input
                        type="time"
                        aria-label={`${label} closing time`}
                        value={slot?.end ?? '17:00'}
                        onChange={(e) => updateTime(key, 'end', e.target.value)}
                        className={TIME_INPUT}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      {/* The bar is only drawn for a span that actually runs
                          forwards. An end at or before the start is not silently
                          redrawn as an overnight shift — the two times stay
                          visible and say so themselves. */}
                      {span ? (
                        <div className="h-2 max-w-[280px] overflow-hidden rounded-[4px] bg-[var(--co-line-subtle)]">
                          <div
                            className="h-full bg-[var(--co-ink)]"
                            style={{ marginLeft: `${span.left}%`, width: `${span.width}%` }}
                          />
                        </div>
                      ) : (
                        <span className="co-mono text-[10.5px] uppercase tracking-[.08em] text-[var(--co-terracotta-ink)]">
                          Closing time is not after opening
                        </span>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="min-w-0 flex-1">
                    <span className="co-mono text-[10.5px] uppercase tracking-[.08em] text-[var(--co-ink-4)]">
                      Closed
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <CardFoot
          left={`${formatHours(weeklyHours)} across ${openDays} day${openDays === 1 ? '' : 's'}`}
          note="Holidays below are subtracted from this on the day they fall."
        />
      </div>

      {/* Holidays */}
      <div>
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h4 className="text-[13.5px] font-semibold text-[var(--co-ink)]">Holidays</h4>
            {/* A holiday is a single date, so a two-day closure is two entries.
                Ranges would make a half-day ambiguous, and an ambiguous closure
                is an SLA clock nobody can predict. */}
            <p className="mt-[4px] text-[12px] leading-[1.5] text-[var(--co-ink-3)]">
              One date each — a two-day closure is two entries.
            </p>
          </div>
          <KitButton onClick={addHoliday}>
            <Plus className="h-[15px] w-[15px]" aria-hidden="true" />
            Add date
          </KitButton>
        </div>
        {holidays.length === 0 ? (
          <p className="text-[12.5px] text-[var(--co-ink-4)]">No holidays configured</p>
        ) : (
          <div className="space-y-2">
            {holidays.map((holiday, index) => (
              <div
                key={index}
                className="flex items-center gap-2 rounded-lg border border-[var(--co-line)] bg-[var(--co-raised)] px-[10px] py-[8px]"
              >
                <input
                  type="text"
                  value={holiday.name}
                  onChange={(e) => updateHoliday(index, 'name', e.target.value)}
                  placeholder="Holiday name"
                  aria-label={`Holiday ${index + 1} name`}
                  className={`${TIME_INPUT} min-w-0 flex-1 text-left`}
                />
                <input
                  type="date"
                  value={holiday.date}
                  onChange={(e) => updateHoliday(index, 'date', e.target.value)}
                  aria-label={`Holiday ${index + 1} date`}
                  className={TIME_INPUT}
                />
                <label className="flex flex-none items-center gap-[6px] whitespace-nowrap text-[11.5px] text-[var(--co-ink-2)]">
                  <input
                    type="checkbox"
                    checked={holiday.recurring || false}
                    onChange={(e) => updateHoliday(index, 'recurring', e.target.checked)}
                    className="h-[13px] w-[13px] rounded accent-[var(--co-brass-ink)]"
                  />
                  Yearly
                </label>
                <IconButton
                  label={`Remove holiday ${index + 1}`}
                  onClick={() => removeHoliday(index)}
                  className="text-[var(--co-burgundy)] hover:border-[var(--co-burgundy)]"
                >
                  <Trash2 className="h-[15px] w-[15px]" aria-hidden="true" />
                </IconButton>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 border-t border-[var(--co-line-subtle)] pt-4">
        <KitButton onClick={onCancel} disabled={isLoading}>
          Cancel
        </KitButton>
        <KitButton
          variant="primary"
          type="submit"
          disabled={isLoading || !name.trim() || !isDirty}
          disabledReason={
            !isLoading && name.trim() && !isDirty && schedule ? 'No changes to save' : undefined
          }
        >
          {isLoading ? (
            <>
              <Spinner size="sm" />
              Saving…
            </>
          ) : (
            <>
              <Check className="h-4 w-4" aria-hidden="true" />
              {schedule ? 'Update Schedule' : 'Create Schedule'}
            </>
          )}
        </KitButton>
      </div>
    </form>
  );
}

function summarizeDay(day: BusinessHoursSchedule['schedule'][DayOfWeek]): string {
  if (!day?.enabled || day.slots.length === 0) return 'Closed';
  const s = day.slots[0];
  return `${s.start}–${s.end}`;
}

interface BusinessHoursCardProps {
  schedule: BusinessHoursSchedule;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
  onSetDefault: () => void;
}

function BusinessHoursCard({
  schedule,
  onEdit,
  onDelete,
  onToggleActive,
  onSetDefault,
}: BusinessHoursCardProps) {
  return (
    <div
      className={`overflow-hidden ${CARD_CLASS}`}
      style={{
        ...CARD,
        background: schedule.isActive ? undefined : 'var(--co-sunken)',
      }}
    >
      <div className="flex items-start justify-between gap-4 px-[18px] pb-[14px] pt-4">
        <div className="flex min-w-0 items-start gap-3">
          <div
            className="mt-[1px] flex h-7 w-7 flex-none items-center justify-center rounded-[7px]"
            style={{
              background: schedule.isActive ? 'var(--co-brass-wash)' : 'var(--co-line-subtle)',
            }}
          >
            <CalendarClock
              className="h-4 w-4"
              style={{
                color: schedule.isActive ? 'var(--co-brass-ink)' : 'var(--co-ink-4)',
              }}
              aria-hidden="true"
            />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-[8px]">
              <h3 className="truncate text-[14px] font-semibold text-[var(--co-ink)]">
                {schedule.name}
              </h3>
              {schedule.isDefault && (
                <StatusChip tone="ok">
                  <Star className="h-[10px] w-[10px]" aria-hidden="true" />
                  Default
                </StatusChip>
              )}
              {!schedule.isActive && <StatusChip tone="neutral">Disabled</StatusChip>}
            </div>
            <p className="co-mono mt-[5px] text-[10.5px] tracking-[.06em] text-[var(--co-ink-3)]">
              {schedule.timezone}
            </p>
            {schedule.description && (
              <p className="mt-[5px] text-[12.5px] leading-[1.5] text-[var(--co-ink-3)]">
                {schedule.description}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-none items-center gap-2">
          {/* `title` stays the bare verb while `aria-label` names the row:
              a tooltip repeating the schedule name on every button is noise,
              but three unlabelled "Edit"s in a list is a screen reader with
              nothing to distinguish them by. */}
          <IconButton
            label={schedule.isActive ? `Disable ${schedule.name}` : `Enable ${schedule.name}`}
            title={schedule.isActive ? 'Disable' : 'Enable'}
            onClick={onToggleActive}
          >
            {schedule.isActive ? (
              <ToggleRight className="h-[15px] w-[15px]" style={{ color: 'var(--co-green-ink)' }} />
            ) : (
              <ToggleLeft className="h-[15px] w-[15px]" />
            )}
          </IconButton>
          <IconButton label={`Edit ${schedule.name}`} title="Edit" onClick={onEdit}>
            <Edit2 className="h-[15px] w-[15px]" aria-hidden="true" />
          </IconButton>
          {!schedule.isDefault && (
            <IconButton
              label={`Delete ${schedule.name}`}
              title="Delete"
              onClick={onDelete}
              className="text-[var(--co-burgundy)] hover:border-[var(--co-burgundy)]"
            >
              <Trash2 className="h-[15px] w-[15px]" aria-hidden="true" />
            </IconButton>
          )}
        </div>
      </div>

      {/* Weekly summary — every day listed, including the closed ones. A week
          that only shows its open days hides the fact that a day was turned off. */}
      <div className="grid grid-cols-2 gap-x-6 border-t border-[var(--co-line-subtle)] px-[18px] py-[13px] sm:grid-cols-4 2xl:grid-cols-7 2xl:gap-x-4">
        {DAYS.map(({ key, label }) => {
          const text = summarizeDay(schedule.schedule[key]);
          const closed = text === 'Closed';
          return (
            <div key={key} className="flex items-baseline gap-3 py-[3px]">
              <span className="co-mono w-8 flex-none text-[10px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
                {label.slice(0, 3)}
              </span>
              <span
                className="co-mono text-[11px] tabular-nums"
                style={{
                  color: closed ? 'var(--co-ink-4)' : 'var(--co-ink)',
                  fontWeight: closed ? 400 : 600,
                }}
              >
                {text}
              </span>
            </div>
          );
        })}
      </div>

      {(schedule.holidays?.length || !schedule.isDefault) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-[var(--co-line)] bg-[var(--co-selected)] px-[18px] py-[11px]">
          <span className="co-mono text-[10.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
            {schedule.holidays?.length
              ? `${schedule.holidays.length} holiday${schedule.holidays.length === 1 ? '' : 's'}`
              : 'No holidays'}
          </span>
          <div className="flex-1" />
          {!schedule.isDefault && (
            <button
              type="button"
              onClick={onSetDefault}
              className="co-mono inline-flex items-center gap-[6px] text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--co-brass-ink)] hover:text-[var(--co-ink)]"
            >
              <Star className="h-[12px] w-[12px]" aria-hidden="true" />
              Set as default
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function BusinessHoursSettings() {
  const {
    schedules,
    isLoading,
    isSaving,
    error,
    fetchSchedules,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    fetchUsage,
    toggleActive,
    setAsDefault,
  } = useBusinessHoursStore();
  const toast = useToast();

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<BusinessHoursSchedule | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [toDelete, setToDelete] = useState<BusinessHoursSchedule | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // What the schedule pending deletion is wired into. Loaded when the dialog
  // opens; null once checked and unused, or when the check itself failed.
  const [usage, setUsage] = useState<BusinessHoursUsage | null>(null);
  const [isCheckingUsage, setIsCheckingUsage] = useState(false);
  // Schedule the in-flight usage lookup belongs to, so a slow response for a
  // dismissed dialog can't land on top of a newer one.
  const pendingUsageId = useRef<string | null>(null);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  /**
   * Reason the last store action failed. Read through `getState()` rather than
   * the destructured `error` above, which is the render-time value and is always
   * stale immediately after an await.
   */
  const failureReason = (fallback = 'Please try again.') =>
    useBusinessHoursStore.getState?.().error || fallback;

  const handleCreate = async (data: CreateBusinessHoursDto) => {
    const result = await createSchedule(data);
    if (result) {
      toast.success('Schedule created', `"${result.name}" is ready to use.`);
      setShowForm(false);
    } else {
      toast.error('Could not create schedule', failureReason());
    }
  };

  const handleUpdate = async (data: CreateBusinessHoursDto) => {
    if (!editing) return;
    const result = await updateSchedule(editing.id, data);
    if (result) {
      toast.success('Schedule updated', `Changes to "${result.name}" were saved.`);
      setEditing(null);
    } else {
      toast.error('Could not update schedule', failureReason());
    }
  };

  /**
   * Open the delete dialog and, in parallel, find out what the schedule is wired
   * into. Deleting a schedule that an SLA policy or pipeline points at doesn't
   * fail — the reference is simply dropped and those flows fall back to calendar
   * time — so the warning is the only thing standing between the user and a
   * silent change to SLA timers.
   */
  const askToDelete = async (schedule: BusinessHoursSchedule) => {
    setToDelete(schedule);
    setShowDeleteConfirm(true);
    setUsage(null);
    setIsCheckingUsage(true);
    pendingUsageId.current = schedule.id;

    const found = await fetchUsage(schedule.id);

    // Drop a result that arrived after the dialog moved on to another schedule
    // (or was dismissed), so the warning can never describe the wrong one.
    if (pendingUsageId.current !== schedule.id) return;
    setUsage(found);
    setIsCheckingUsage(false);
  };

  const closeDeleteDialog = () => {
    pendingUsageId.current = null;
    setShowDeleteConfirm(false);
    setToDelete(null);
    setUsage(null);
    setIsCheckingUsage(false);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setIsDeleting(true);
    // Captured before the delete — `toDelete` is cleared on success.
    const deletedName = toDelete.name;
    const success = await deleteSchedule(toDelete.id);
    if (success) {
      toast.success('Schedule deleted', `"${deletedName}" was removed.`);
      closeDeleteDialog();
    } else {
      toast.error('Could not delete schedule', failureReason());
    }
    setIsDeleting(false);
  };

  // `schedule.isActive` is the state BEFORE the toggle, so an active schedule
  // here means we just disabled it.
  const handleToggleActive = async (schedule: BusinessHoursSchedule) => {
    const wasActive = schedule.isActive;
    const ok = await toggleActive(schedule.id);
    if (ok) {
      toast.success(
        wasActive ? 'Schedule disabled' : 'Schedule enabled',
        wasActive
          ? `"${schedule.name}" no longer applies to SLA timers.`
          : `"${schedule.name}" now applies to SLA timers.`,
      );
    } else {
      toast.error(
        wasActive ? 'Could not disable schedule' : 'Could not enable schedule',
        failureReason(),
      );
    }
  };

  const handleSetDefault = async (schedule: BusinessHoursSchedule) => {
    const ok = await setAsDefault(schedule.id);
    if (ok) {
      toast.success('Default schedule updated', `"${schedule.name}" is now the default.`);
    } else {
      toast.error('Could not set default schedule', failureReason());
    }
  };

  if (isLoading && schedules.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[14px]">
      <SettingsCard>
        <SectionHead
          title="Business hours"
          description="Working-hours schedules — time zone, weekly hours, holidays. SLA policies and pipelines pick which one to follow; a policy that follows none is measured in calendar time."
          actions={
            <KitButton
              variant="primary"
              onClick={() => setShowForm(true)}
              disabled={showForm || editing !== null}
            >
              <Plus className="h-[15px] w-[15px]" aria-hidden="true" />
              New Schedule
            </KitButton>
          }
        />
      </SettingsCard>

      {/* Error */}
      {error && <Notice tone="danger" headline="Something went wrong" body={error} />}

      {/* Create form */}
      {showForm && (
        <SettingsCard
          style={{ borderColor: 'var(--co-brass-line)', background: 'var(--co-brass-wash)' }}
        >
          <h3 className="co-mono mb-4 text-[10px] font-semibold uppercase tracking-[.16em] text-[var(--co-brass-ink)]">
            New business hours schedule
          </h3>
          <BusinessHoursForm
            onSubmit={handleCreate}
            onCancel={() => setShowForm(false)}
            isLoading={isSaving}
          />
        </SettingsCard>
      )}

      {/* Edit dialog — editing opens in a popup rather than an inline panel, so
          the schedule is never shown twice (form + its own read-only card). */}
      <Modal
        isOpen={editing !== null}
        onClose={() => setEditing(null)}
        title="Edit Business Hours Schedule"
        size="xl"
      >
        {editing && (
          <BusinessHoursForm
            schedule={editing}
            onSubmit={handleUpdate}
            onCancel={() => setEditing(null)}
            isLoading={isSaving}
          />
        )}
      </Modal>

      {/* List */}
      <div className="flex flex-col gap-[14px]">
        {schedules.map((schedule) => (
          <BusinessHoursCard
            key={schedule.id}
            schedule={schedule}
            onEdit={() => setEditing(schedule)}
            onDelete={() => askToDelete(schedule)}
            onToggleActive={() => handleToggleActive(schedule)}
            onSetDefault={() => handleSetDefault(schedule)}
          />
        ))}

        {schedules.length === 0 && !showForm && (
          <div className={`overflow-hidden ${CARD_CLASS}`} style={CARD}>
            <EmptyState
              title="No business hours schedules"
              body="Until one exists, every SLA target is counted in calendar hours — an overnight ticket burns its clock while nobody is working."
              action={
                <KitButton variant="primary" onClick={() => setShowForm(true)}>
                  <Plus className="h-[15px] w-[15px]" aria-hidden="true" />
                  Create First Schedule
                </KitButton>
              }
            />
          </div>
        )}
      </div>

      {/* Delete confirmation — escalates to an in-use warning naming the SLA
          policies and pipelines that would be affected. */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={closeDeleteDialog}
        title="Delete Business Hours Schedule"
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div
              className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-lg"
              style={{ background: 'var(--co-burgundy-wash)' }}
            >
              <AlertTriangle
                className="h-5 w-5"
                style={{ color: 'var(--co-burgundy)' }}
                aria-hidden="true"
              />
            </div>
            <div className="min-w-0">
              <h3
                className="text-[16px] font-bold tracking-[-.01em] text-[var(--co-ink)]"
                style={{ fontFamily: 'var(--co-font-display)' }}
              >
                Delete &quot;{toDelete?.name}&quot;?
              </h3>
              {/* Plain confirmation. The consequences of deleting a schedule
                  that's actually wired into something are spelled out in the
                  in-use panel below, so repeating them here would warn about
                  SLA policies falling back to calendar time even when no policy
                  references this schedule at all. */}
              <p className="mt-2 text-[12.5px] leading-[1.55] text-[var(--co-ink-3)]">
                This action cannot be undone.
                {usage && !usage.inUse && ' Nothing is currently using this schedule.'}
              </p>
            </div>
          </div>

          {isCheckingUsage && (
            <div className="mt-4 flex items-center gap-2 text-[12.5px] text-[var(--co-ink-3)]">
              <Spinner size="sm" />
              Checking where this schedule is used…
            </div>
          )}

          {/* In use — say so plainly, and name what breaks, because nothing else
              will: the delete succeeds either way and those flows quietly switch
              to calendar time. */}
          {!isCheckingUsage && usage?.inUse && (
            <div className="mt-4">
              <Notice
                tone="warn"
                headline="This schedule is already in use"
                body={
                  <>
                    {usage.slaPolicies.length > 0 && (
                      <span className="block">
                        <strong className="font-semibold">
                          SLA {usage.slaPolicies.length === 1 ? 'policy' : 'policies'} (
                          {usage.slaPolicies.length}):
                        </strong>{' '}
                        {usage.slaPolicies.map((p) => p.name).join(', ')}
                      </span>
                    )}
                    {usage.pipelines.length > 0 && (
                      <span className="mt-1 block">
                        <strong className="font-semibold">
                          {usage.pipelines.length === 1 ? 'Pipeline' : 'Pipelines'} (
                          {usage.pipelines.length}):
                        </strong>{' '}
                        {usage.pipelines.map((p) => p.name).join(', ')}
                      </span>
                    )}
                    <span className="mt-2 block">
                      Their response and resolution timers will be measured in calendar hours until
                      another schedule is selected.
                    </span>
                  </>
                }
              />
            </div>
          )}

          <div className="mt-6 flex justify-end gap-3">
            <KitButton onClick={closeDeleteDialog} disabled={isDeleting}>
              Cancel
            </KitButton>
            <KitButton
              variant="destructive"
              onClick={handleDelete}
              // Held until the check resolves, so the button can't be clicked
              // before the warning it belongs to has had a chance to appear.
              disabled={isDeleting || isCheckingUsage}
            >
              {isDeleting ? (
                <>
                  <Spinner size="sm" />
                  Deleting…
                </>
              ) : (
                <>
                  {/* Risk icon in place of the plain bin once we know something
                      depends on this schedule. */}
                  {usage?.inUse ? (
                    <ShieldAlert className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  )}
                  {usage?.inUse ? 'Delete Anyway' : 'Delete Schedule'}
                </>
              )}
            </KitButton>
          </div>
        </div>
      </Modal>
    </div>
  );
}
